import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, HelpCircle, ChevronLeft, ChevronRight, Check, Calendar, FileText, CheckCircle2 } from 'lucide-react';
import { CreateTieredPlanRequest, TrialPeriod } from '@/services/adminPaymentService';
import { adminPaymentService } from '@/services/adminPaymentService';
import { toastHelper } from '@/utils/toastHelper';

interface CreateTieredPlanModalProps {
  onCreatePlan: (planData: CreateTieredPlanRequest) => Promise<void>;
}

type PlanSelection = 'monthly' | 'annual' | 'both';

const CreateTieredPlanModal: React.FC<CreateTieredPlanModalProps> = ({ onCreatePlan }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Plan selection
  const [planSelection, setPlanSelection] = useState<PlanSelection>('both');
  
  // Step 2: Base info (shared across all plans)
  const [baseInfo, setBaseInfo] = useState({
    name: '',
    description: '',
    currency_code: 'USD',
    subscription_tier: 'starter' as string,
    tax_calculation: 'no_tax' as 'no_tax' | 'add_tax' | 'include_tax',
    tax_percentage: undefined as number | undefined,
    payment_failure_threshold: 1,
    auto_bill_outstanding: true,
    has_setup_fee: false,
    setup_fee: undefined as number | undefined,
    has_trial: false,
    trial_periods: [] as TrialPeriod[],
    unlimited_cycles: true,
    total_cycles: undefined as number | undefined,
    interval_count: 1
  });
  
  // Step 3 & 4: Plan-specific pricing tiers
  const [monthlyTiers, setMonthlyTiers] = useState<Array<{
    tier_name: string;
    base_price: number;
    discount_percentage?: number;
    currency_code: string;
    has_promotion: boolean;
    promotional_price?: number;
    promotion_duration_months?: number;
  }>>([
    {
      tier_name: 'Starter',
      base_price: 0,
      currency_code: 'USD',
      has_promotion: false
    }
  ]);

  const [annualTiers, setAnnualTiers] = useState<Array<{
    tier_name: string;
    base_price: number;
    discount_percentage?: number;
    currency_code: string;
    has_promotion: boolean;
    promotional_price?: number;
    promotion_duration_months?: number;
  }>>([
    {
      tier_name: 'Starter',
      base_price: 0,
      currency_code: 'USD',
      has_promotion: false
    }
  ]);
  
  // Annual plan pricing mode: 'auto' (10x) or 'manual' (custom multiplier)
  const [annualPricingMode, setAnnualPricingMode] = useState<'auto' | 'manual'>('auto');
  const [annualMultiplier, setAnnualMultiplier] = useState<number>(10);


  const resetWizard = () => {
    setCurrentStep(1);
    setPlanSelection('both');
    setBaseInfo({
      name: '',
      description: '',
      currency_code: 'USD',
      subscription_tier: 'starter',
      tax_calculation: 'no_tax',
      tax_percentage: undefined,
      payment_failure_threshold: 1,
      auto_bill_outstanding: true,
      has_setup_fee: false,
      setup_fee: undefined,
      has_trial: false,
      trial_periods: [],
      unlimited_cycles: true,
      total_cycles: undefined,
      interval_count: 1
    });
    setMonthlyTiers([{
      tier_name: 'Starter',
      base_price: 0,
      currency_code: 'USD',
      has_promotion: false
    }]);
    setAnnualTiers([{
      tier_name: 'Starter',
      base_price: 0,
      currency_code: 'USD',
      has_promotion: false
    }]);
  };

  // Calculate discount percentage
  const calculateDiscountPercentage = (promotionalPrice: number | undefined, basePrice: number): number | undefined => {
    if (basePrice > 0 && promotionalPrice && promotionalPrice < basePrice) {
      return Math.round(((basePrice - promotionalPrice) / basePrice) * 100 * 100) / 100; // Round to 2 decimal places
    }
    return undefined;
  };

  // Calculate annual saving percentage (vs 12x monthly base price)
  const calculateAnnualSaving = (annualBasePrice: number, monthlyBasePrice: number): number | undefined => {
    if (!monthlyBasePrice || monthlyBasePrice <= 0) return undefined;
    const twelveMonthPrice = monthlyBasePrice * 12;
    if (annualBasePrice >= twelveMonthPrice) return undefined;
    const saving = ((twelveMonthPrice - annualBasePrice) / twelveMonthPrice) * 100;
    return Math.round(saving * 100) / 100; // Round to 2 decimal places
  };


  // Tier handlers
  const handleAddTier = (isMonthly: boolean) => {
    const tiers = isMonthly ? monthlyTiers : annualTiers;
    const setTiers = isMonthly ? setMonthlyTiers : setAnnualTiers;

    setTiers([
      ...tiers,
      {
        tier_name: `Tier ${tiers.length + 1}`,
        base_price: 0,
        currency_code: baseInfo.currency_code,
        has_promotion: false
      }
    ]);
  };

  const handleRemoveTier = (index: number, isMonthly: boolean) => {
    const tiers = isMonthly ? monthlyTiers : annualTiers;
    const setTiers = isMonthly ? setMonthlyTiers : setAnnualTiers;
    if (tiers.length <= 1) {
      toastHelper.newMessage('error', 'Error', 'At least one tier is required');
      return;
    }
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const handleTierChange = (index: number, field: 'tier_name' | 'base_price', value: string | number, isMonthly: boolean) => {
    const tiers = isMonthly ? monthlyTiers : annualTiers;
    const setTiers = isMonthly ? setMonthlyTiers : setAnnualTiers;
    const updatedTiers = [...tiers];
    const updatedTier = {
      ...updatedTiers[index],
      [field]: value
    };

    // Calculate discount percentage if promotional pricing is enabled
    if (field === 'base_price') {
      const basePrice = typeof value === 'number' ? value : parseFloat(value.toString()) || 0;
      updatedTier.discount_percentage = calculateDiscountPercentage(updatedTier.promotional_price, basePrice);
    }

    updatedTiers[index] = updatedTier;
    setTiers(updatedTiers);
  };

  // Trial period handlers removed - promotional pricing is now configured per-tier in Step 3

  // Validation
  const validateBaseInfo = (): boolean => {
    if (!baseInfo.name.trim()) {
      toastHelper.newMessage('error', 'Error', 'Plan name is required');
      return false;
    }
    if (!baseInfo.description.trim()) {
      toastHelper.newMessage('error', 'Error', 'Plan description is required');
      return false;
    }
    if (baseInfo.tax_calculation !== 'no_tax' && (!baseInfo.tax_percentage || baseInfo.tax_percentage <= 0)) {
      toastHelper.newMessage('error', 'Error', 'Tax percentage is required when tax calculation is enabled');
      return false;
    }
    if (baseInfo.has_setup_fee && (!baseInfo.setup_fee || baseInfo.setup_fee <= 0)) {
      toastHelper.newMessage('error', 'Error', 'Setup fee amount is required when setup fee is enabled');
      return false;
    }
    if (!baseInfo.unlimited_cycles && (!baseInfo.total_cycles || baseInfo.total_cycles <= 0)) {
      toastHelper.newMessage('error', 'Error', 'Total cycles is required when billing cycles are limited');
      return false;
    }
    return true;
  };

  const validateTiers = (tiers: typeof monthlyTiers, planType: string): boolean => {
    if (tiers.length === 0) {
      toastHelper.newMessage('error', 'Error', `At least one pricing tier is required for ${planType} plan`);
      return false;
    }
    
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      if (!tier.tier_name || !tier.tier_name.trim()) {
        toastHelper.newMessage('error', 'Error', `${planType} plan - Tier ${i + 1}: Tier name is required`);
        return false;
      }
      if (tier.base_price <= 0) {
        toastHelper.newMessage('error', 'Error', `${planType} plan - Tier ${i + 1}: Base price must be greater than 0`);
        return false;
      }

      // Validate promotional pricing
      if (tier.has_promotion) {
        if (!tier.promotional_price || tier.promotional_price <= 0) {
          toastHelper.newMessage('error', 'Error', `${planType} plan - Tier ${i + 1}: Promotional price is required and must be greater than 0`);
          return false;
        }
        if (tier.promotional_price >= tier.base_price) {
          toastHelper.newMessage('error', 'Error', `${planType} plan - Tier ${i + 1}: Promotional price must be less than base price`);
          return false;
        }
        if (!tier.promotion_duration_months || tier.promotion_duration_months < 1 || tier.promotion_duration_months > 12) {
          toastHelper.newMessage('error', 'Error', `${planType} plan - Tier ${i + 1}: Promotion duration must be between 1 and 12 months`);
          return false;
        }
      }

      // For annual plans, validate against 12x monthly base price
      if (planType === 'Annual') {
        const monthlyTier = monthlyTiers.find(mt => mt.tier_name === tier.tier_name);
        if (monthlyTier) {
          const maxAnnualBasePrice = monthlyTier.base_price * 12;
          if (tier.base_price > maxAnnualBasePrice) {
            toastHelper.newMessage('error', 'Error', `${planType} plan - Tier ${i + 1}: Base price cannot exceed 12x monthly base price (${maxAnnualBasePrice.toFixed(2)})`);
            return false;
          }
        }
      }
    }
    return true;
  };

  // Navigation
  const handleNext = () => {
    if (currentStep === 1) {
      // Step 1 -> Step 2: No validation needed
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Step 2 -> Step 3: Validate base info
      if (!validateBaseInfo()) return;
      
      // Update currency in tiers
      setMonthlyTiers(prev => prev.map(t => ({ ...t, currency_code: baseInfo.currency_code })));
      setAnnualTiers(prev => prev.map(t => ({ ...t, currency_code: baseInfo.currency_code })));
      
      // Determine next step based on selection
      if (planSelection === 'monthly') {
        setCurrentStep(3);
      } else if (planSelection === 'annual') {
        setCurrentStep(4);
      } else {
        setCurrentStep(3); // both -> start with monthly
      }
    } else if (currentStep === 3) {
      // Step 3 (Monthly) -> Step 4 or 5
      if (!validateTiers(monthlyTiers, 'Monthly')) return;
      
      if (planSelection === 'both') {
        // Populate annual tiers based on pricing mode
        const multiplier = annualPricingMode === 'auto' ? 10 : annualMultiplier;
        const defaultedAnnualTiers = monthlyTiers.map(tier => ({
          tier_name: tier.tier_name,
          base_price: Math.round((tier.base_price * multiplier) * 100) / 100,
          currency_code: baseInfo.currency_code,
          has_promotion: tier.has_promotion,
          promotional_price: tier.promotional_price ? Math.round((tier.promotional_price * multiplier) * 100) / 100 : undefined,
          promotion_duration_months: tier.promotion_duration_months
        }));
        setAnnualTiers(defaultedAnnualTiers);
        setCurrentStep(4); // Go to annual
      } else {
        setCurrentStep(5); // Go to verification
      }
    } else if (currentStep === 4) {
      // Step 4 (Annual) -> Step 5
      if (!validateTiers(annualTiers, 'Annual')) return;
      setCurrentStep(5);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setCurrentStep(2);
    } else if (currentStep === 4) {
      if (planSelection === 'both') {
        setCurrentStep(3); // Back to monthly
      } else {
        setCurrentStep(2); // Back to base info
      }
    } else if (currentStep === 5) {
      if (planSelection === 'both') {
        setCurrentStep(4); // Back to annual
      } else if (planSelection === 'monthly') {
        setCurrentStep(3); // Back to monthly
      } else {
        setCurrentStep(4); // Back to annual
      }
    }
  };

  const getTotalSteps = (): number => {
    if (planSelection === 'both') return 5;
    return 4;
  };

  const handleCreatePlans = async () => {
    try {
      setLoading(true);
      
      // Prepare monthly plan if selected
      let monthlyPlanData = null;
      if (planSelection === 'monthly' || planSelection === 'both') {
        // Check if any tier has promotional pricing enabled
        const tierWithPromotion = monthlyTiers.find(t => t.has_promotion);
        const hasTrialPeriods = tierWithPromotion !== undefined;

        // Convert promotional pricing to trial periods
        const trialPeriods: TrialPeriod[] = hasTrialPeriods && tierWithPromotion ? [{
          price: tierWithPromotion.promotional_price!,
          interval_unit: 'MONTH',
          interval_count: 1,
          cycles: tierWithPromotion.promotion_duration_months!
        }] : [];

        monthlyPlanData = {
          name: baseInfo.name,
          description: baseInfo.description,
          currency_code: baseInfo.currency_code,
          has_setup_fee: baseInfo.has_setup_fee,
          setup_fee: baseInfo.setup_fee,
          has_trial: hasTrialPeriods,
          trial_periods: trialPeriods,
          billing_cycle: 'monthly',
          interval_count: baseInfo.interval_count,
          tiers: monthlyTiers.map((tier) => {
            // Use promotional_price as discounted_price if promotion is enabled
            const discountedPrice = tier.has_promotion && tier.promotional_price && tier.promotional_price < tier.base_price
              ? tier.promotional_price
              : undefined;

            return {
              tier_name: tier.tier_name,
              starting_quantity: '1',
              ending_quantity: undefined, // Fixed price per tier, no quantity range
              base_price: tier.base_price,
              discounted_price: discountedPrice,
              currency_code: tier.currency_code
            };
          }),
          unlimited_cycles: baseInfo.unlimited_cycles,
          total_cycles: baseInfo.total_cycles,
          tax_calculation: baseInfo.tax_calculation,
          tax_percentage: baseInfo.tax_percentage,
          payment_failure_threshold: baseInfo.payment_failure_threshold,
          auto_bill_outstanding: baseInfo.auto_bill_outstanding,
          setup_fee_failure_action: 'CONTINUE',
          subscription_tier: baseInfo.subscription_tier
        };
      }

      // Prepare annual plan if selected
      let annualPlanData = null;
      if (planSelection === 'annual' || planSelection === 'both') {
        // Check if any tier has promotional pricing enabled
        const tierWithPromotion = annualTiers.find(t => t.has_promotion);
        const hasTrialPeriods = tierWithPromotion !== undefined;

        // Convert promotional pricing to trial periods (annual uses YEAR interval)
        const trialPeriods: TrialPeriod[] = hasTrialPeriods && tierWithPromotion ? [{
          price: tierWithPromotion.promotional_price!,
          interval_unit: 'YEAR',
          interval_count: 1,
          cycles: tierWithPromotion.promotion_duration_months! // For annual, this represents years
        }] : [];

        annualPlanData = {
          name: baseInfo.name,
          description: baseInfo.description,
          currency_code: baseInfo.currency_code,
          has_setup_fee: baseInfo.has_setup_fee,
          setup_fee: baseInfo.setup_fee,
          has_trial: hasTrialPeriods,
          trial_periods: trialPeriods,
          billing_cycle: 'annual',
          interval_count: baseInfo.interval_count,
          tiers: annualTiers.map((tier) => {
            // Use promotional_price as discounted_price if promotion is enabled
            const discountedPrice = tier.has_promotion && tier.promotional_price && tier.promotional_price < tier.base_price
              ? tier.promotional_price
              : undefined;

            return {
              tier_name: tier.tier_name,
              starting_quantity: '1',
              ending_quantity: undefined, // Fixed price per tier, no quantity range
              base_price: tier.base_price,
              discounted_price: discountedPrice,
              currency_code: tier.currency_code
            };
          }),
          unlimited_cycles: baseInfo.unlimited_cycles,
          total_cycles: baseInfo.total_cycles,
          tax_calculation: baseInfo.tax_calculation,
          tax_percentage: baseInfo.tax_percentage,
          payment_failure_threshold: baseInfo.payment_failure_threshold,
          auto_bill_outstanding: baseInfo.auto_bill_outstanding,
          setup_fee_failure_action: 'CONTINUE',
          subscription_tier: baseInfo.subscription_tier
        };
      }

      // Call the multiple plans endpoint
      const response = await adminPaymentService.createMultiplePlans({
        monthly_plan: monthlyPlanData,
        annual_plan: annualPlanData,
        shared_name: baseInfo.name,
        shared_description: baseInfo.description,
        currency_code: baseInfo.currency_code
      });

      toastHelper.newMessage('success', 'Success', response.message || 'Plans created successfully');
      setIsOpen(false);
      resetWizard();
    } catch (error: any) {
      // Log error to console instead of blocking UI
      console.error('Failed to create plans:', error);
      console.error('Error details:', error.response?.data);
      
      // Show user-friendly error message
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to create plans';
      toastHelper.newMessage('error', 'Error', errorMessage);
      
      // Don't throw error to prevent React error boundary from catching it
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Initialization - Choose plan type
  const renderStep1 = () => {
    return (
      <div className="space-y-6 py-4 px-2">
        <div className="text-center space-y-2">
          <Label className="text-lg font-semibold">Select Plan Type</Label>
          <p className="text-sm text-muted-foreground">Choose which subscription plans you want to create</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:border-primary ${planSelection === 'monthly' ? 'border-primary bg-primary/5' : ''}`}
            onClick={() => setPlanSelection('monthly')}
          >
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-2">
                <Calendar className="h-8 w-8 text-muted-foreground" />
                <Label className="text-base font-semibold">Monthly Only</Label>
                <p className="text-xs text-center text-muted-foreground">Create a monthly subscription plan</p>
                {planSelection === 'monthly' && (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:border-primary ${planSelection === 'annual' ? 'border-primary bg-primary/5' : ''}`}
            onClick={() => setPlanSelection('annual')}
          >
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-2">
                <Calendar className="h-8 w-8 text-muted-foreground" />
                <Label className="text-base font-semibold">Annual Only</Label>
                <p className="text-xs text-center text-muted-foreground">Create an annual subscription plan</p>
                {planSelection === 'annual' && (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:border-primary ${planSelection === 'both' ? 'border-primary bg-primary/5' : ''}`}
            onClick={() => setPlanSelection('both')}
          >
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-2">
                <Calendar className="h-8 w-8 text-muted-foreground" />
                <Label className="text-base font-semibold">Both</Label>
                <p className="text-xs text-center text-muted-foreground">Create both monthly and annual plans</p>
                {planSelection === 'both' && (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Step 2: Base Info
  const renderStep2 = () => {
    return (
      <div className="space-y-6 py-4 px-2">
        <div className="space-y-2">
          <Label className="text-lg font-semibold">Base Information</Label>
          <p className="text-sm text-muted-foreground">Shared settings that apply to all plans</p>
        </div>

        {/* Name and Description */}
        <div className="space-y-4">
          <div>
            <Label>Plan Name *</Label>
            <Input
              value={baseInfo.name}
              onChange={(e) => setBaseInfo({ ...baseInfo, name: e.target.value })}
              placeholder="Platinum Plan"
              maxLength={127}
            />
            <p className="text-xs text-muted-foreground mt-1">{baseInfo.name.length}/127</p>
          </div>
          
          <div>
            <Label>Description *</Label>
            <Textarea
              value={baseInfo.description}
              onChange={(e) => setBaseInfo({ ...baseInfo, description: e.target.value })}
              placeholder="Tiered based pricing for technical support"
              maxLength={127}
            />
            <p className="text-xs text-muted-foreground mt-1">{baseInfo.description.length}/127</p>
          </div>

          <div>
            <Label>Subscription Tier *</Label>
            <Select
              value={baseInfo.subscription_tier}
              onValueChange={(value) => setBaseInfo({ ...baseInfo, subscription_tier: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="family">Family</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Which subscription tier does this plan belong to?</p>
          </div>
        </div>

        {/* Currency and Setup Fee */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
          <div className="space-y-2">
            <Label>Currency</Label>
            <Select
              value={baseInfo.currency_code}
              onValueChange={(value) => setBaseInfo({ ...baseInfo, currency_code: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="CAD">CAD</SelectItem>
                <SelectItem value="AUD">AUD</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Setup Fee</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={baseInfo.has_setup_fee}
                onCheckedChange={(checked) => setBaseInfo({ ...baseInfo, has_setup_fee: checked })}
              />
              <span className="text-sm">Charge a one-time setup fee?</span>
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </div>
            {baseInfo.has_setup_fee && (
              <Input
                type="number"
                step="0.01"
                min="0"
                value={baseInfo.setup_fee || ''}
                onChange={(e) => setBaseInfo({ ...baseInfo, setup_fee: parseFloat(e.target.value) || undefined })}
                placeholder="Set-up fee"
              />
            )}
          </div>
        </div>

        {/* Billing Frequency and Cycles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
          <div className="space-y-2">
            <Label>Billing Frequency</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="1"
                value={1}
                disabled
                className="w-20 bg-muted cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground flex-1">
                Fixed at 1. Applies to both monthly (1 month) and annual (1 year) plans.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Billing Cycles</Label>
            <RadioGroup
              value={baseInfo.unlimited_cycles ? 'unlimited' : 'limited'}
              onValueChange={(value) => setBaseInfo({ ...baseInfo, unlimited_cycles: value === 'unlimited' })}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="unlimited" id="unlimited" />
                <Label htmlFor="unlimited" className="font-normal">Unlimited billing cycles</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="limited" id="limited" />
                <Label htmlFor="limited" className="font-normal">Limited billing cycles</Label>
              </div>
            </RadioGroup>
            {!baseInfo.unlimited_cycles && (
              <Input
                type="number"
                min="1"
                value={baseInfo.total_cycles || ''}
                onChange={(e) => setBaseInfo({ ...baseInfo, total_cycles: parseInt(e.target.value) || undefined })}
                placeholder="Total cycles"
              />
            )}
          </div>
        </div>

        {/* Tax Calculation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>Calculate the tax for this subscription plan</Label>
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </div>
            <Select
              value={baseInfo.tax_calculation}
              onValueChange={(value: 'no_tax' | 'add_tax' | 'include_tax') => setBaseInfo({ ...baseInfo, tax_calculation: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no_tax">No tax</SelectItem>
                <SelectItem value="add_tax">Add tax to price</SelectItem>
                <SelectItem value="include_tax">Include tax in price</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {baseInfo.tax_calculation !== 'no_tax' && (
            <div className="space-y-2">
              <Label>Tax Percentage</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={baseInfo.tax_percentage || ''}
                onChange={(e) => setBaseInfo({ ...baseInfo, tax_percentage: parseFloat(e.target.value) || undefined })}
                placeholder="Tax percentage"
              />
            </div>
          )}
        </div>

        {/* Payment Preferences */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <Label className="text-base font-semibold">Manage your billing preferences</Label>
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>How many missed billing cycles before a subscription is paused?</Label>
              <Input
                type="number"
                min="1"
                value={baseInfo.payment_failure_threshold}
                onChange={(e) => setBaseInfo({ ...baseInfo, payment_failure_threshold: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={baseInfo.auto_bill_outstanding}
                onCheckedChange={(checked) => setBaseInfo({ ...baseInfo, auto_bill_outstanding: checked })}
              />
              <Label>Turn on auto billing of outstanding payments</Label>
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </div>

      </div>
    );
  };

  // Recalculate annual tiers when mode or multiplier changes
  const recalculateAnnualTiers = (mode: 'auto' | 'manual', multiplier: number) => {
    const effectiveMultiplier = mode === 'auto' ? 10 : multiplier;
    const recalculated = monthlyTiers.map((tier, index) => {
      const existingAnnualTier = annualTiers[index];
      // Recalculate base_price from monthly when mode/multiplier changes
      // Preserve manual promotional pricing edits
      return {
        tier_name: tier.tier_name,
        base_price: Math.round((tier.base_price * effectiveMultiplier) * 100) / 100,
        currency_code: baseInfo.currency_code,
        // Preserve existing promotional settings if they were manually edited
        has_promotion: existingAnnualTier?.has_promotion !== undefined ? existingAnnualTier.has_promotion : tier.has_promotion,
        promotional_price: existingAnnualTier?.promotional_price !== undefined ? existingAnnualTier.promotional_price : (tier.promotional_price ? Math.round((tier.promotional_price * effectiveMultiplier) * 100) / 100 : undefined),
        promotion_duration_months: existingAnnualTier?.promotion_duration_months !== undefined ? existingAnnualTier.promotion_duration_months : tier.promotion_duration_months
      };
    });
    setAnnualTiers(recalculated);
  };

  // Recalculate annual tiers when monthly tiers change (if on annual step)
  // Only recalculate base_price, preserve manual promotional edits
  useEffect(() => {
    if (currentStep === 4 && monthlyTiers.length > 0) {
      const multiplier = annualPricingMode === 'auto' ? 10 : annualMultiplier;
      const recalculated = monthlyTiers.map((tier, index) => {
        const existingAnnualTier = annualTiers[index];
        return {
          tier_name: tier.tier_name,
          base_price: Math.round((tier.base_price * multiplier) * 100) / 100,
          currency_code: baseInfo.currency_code,
          // Preserve existing promotional settings if they exist, otherwise initialize from monthly
          has_promotion: existingAnnualTier?.has_promotion !== undefined ? existingAnnualTier.has_promotion : tier.has_promotion,
          promotional_price: existingAnnualTier?.promotional_price !== undefined ? existingAnnualTier.promotional_price : (tier.promotional_price ? Math.round((tier.promotional_price * multiplier) * 100) / 100 : undefined),
          promotion_duration_months: existingAnnualTier?.promotion_duration_months !== undefined ? existingAnnualTier.promotion_duration_months : tier.promotion_duration_months
        };
      });
      setAnnualTiers(recalculated);
    }
  }, [monthlyTiers, currentStep, annualPricingMode, annualMultiplier, baseInfo.currency_code]);

  // Step 3 & 4: Pricing Tiers
  const renderTiersStep = (isMonthly: boolean) => {
    const tiers = isMonthly ? monthlyTiers : annualTiers;
    const planType = isMonthly ? 'Monthly' : 'Annual';
    
    return (
      <div className="space-y-6 py-4 px-2">
        <div className="space-y-2">
          <Label className="text-lg font-semibold">{planType} Plan Pricing</Label>
          <p className="text-sm text-muted-foreground">Define pricing tiers for the {planType.toLowerCase()} plan</p>
        </div>

        {/* Annual Plan Pricing Mode Toggle */}
        {!isMonthly && (
          <div className="space-y-4 p-4 bg-muted/30 rounded border">
            <Label className="text-base font-semibold">Pricing Calculation Method</Label>
            <RadioGroup
              value={annualPricingMode}
              onValueChange={(value) => {
                const newMode = value as 'auto' | 'manual';
                setAnnualPricingMode(newMode);
                recalculateAnnualTiers(newMode, annualMultiplier);
              }}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="auto" id="auto" />
                <Label htmlFor="auto" className="font-normal">Automatic (10x monthly price)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="manual" id="manual" />
                <Label htmlFor="manual" className="font-normal">Manual multiplier</Label>
              </div>
            </RadioGroup>
            {annualPricingMode === 'manual' && (
              <div className="space-y-2 mt-3">
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Multiplier (1-12):</Label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    step="1"
                    value={annualMultiplier}
                    onChange={(e) => {
                      const newMultiplier = Math.max(1, Math.min(12, Math.round(parseFloat(e.target.value) || 1)));
                      setAnnualMultiplier(newMultiplier);
                      recalculateAnnualTiers('manual', newMultiplier);
                    }}
                    className="w-24"
                  />
                  <p className="text-xs text-muted-foreground">
                    Annual base price = Monthly base price × {annualMultiplier}
                  </p>
                </div>
                {monthlyTiers.length > 0 && monthlyTiers[0].base_price > 0 && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Example: {baseInfo.currency_code} {monthlyTiers[0].base_price.toFixed(2)} × {annualMultiplier} = {baseInfo.currency_code} {(monthlyTiers[0].base_price * annualMultiplier).toFixed(2)}</p>
                    <p className="text-green-600">
                      Savings vs 12x: {((1 - annualMultiplier / 12) * 100).toFixed(2)}% ({(monthlyTiers[0].base_price * 12 - monthlyTiers[0].base_price * annualMultiplier).toFixed(2)} {baseInfo.currency_code})
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Tier Based Pricing</Label>
            {isMonthly && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAddTier(isMonthly)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add another tier
              </Button>
            )}
            {!isMonthly && (
              <p className="text-xs text-muted-foreground">
                Annual tiers are automatically calculated from monthly tiers
              </p>
            )}
          </div>
          
          <div className="space-y-3">
            {tiers.map((tier, index) => {
              const discountPercentage = calculateDiscountPercentage(tier.promotional_price, tier.base_price);

              // For annual plans, find corresponding monthly tier and calculate annual saving
              let annualSaving: number | undefined = undefined;
              let maxAnnualPriceError = false;
              if (!isMonthly) {
                const monthlyTier = monthlyTiers.find(mt => mt.tier_name === tier.tier_name);
                if (monthlyTier) {
                  annualSaving = calculateAnnualSaving(tier.base_price, monthlyTier.base_price);
                  const maxAnnualBasePrice = monthlyTier.base_price * 12;
                  maxAnnualPriceError = tier.base_price > maxAnnualBasePrice;
                }
              }
              
              return (
                <div key={index} className="p-4 bg-muted/50 rounded border">
                  <div className="grid grid-cols-9 gap-3 items-start">
                    <div className="col-span-4">
                      <Label className="text-xs">Tier Name</Label>
                      <Input
                        value={tier.tier_name}
                        onChange={(e) => handleTierChange(index, 'tier_name', e.target.value, isMonthly)}
                        placeholder="Starter"
                        className="mt-1"
                        disabled={!isMonthly}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Name of this pricing tier</p>
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Base Price *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={tier.base_price}
                        onChange={(e) => {
                          const newBasePrice = parseFloat(e.target.value) || 0;
                          handleTierChange(index, 'base_price', newBasePrice, isMonthly);
                        }}
                        placeholder="0.00"
                        className={`mt-1 ${maxAnnualPriceError ? 'border-destructive' : ''}`}
                        disabled={!isMonthly}
                        readOnly={!isMonthly}
                      />
                      {maxAnnualPriceError ? (
                        <p className="text-xs text-destructive mt-1">Base price cannot exceed 12x monthly base price</p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">
                          {isMonthly ? 'Regular price (reference for discount)' : 'Calculated from monthly base price × multiplier'}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {isMonthly && tiers.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveTier(index, isMonthly)}
                          className="mt-6"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Promotional Pricing Section */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 mb-3">
                      <Switch
                        checked={tier.has_promotion}
                        onCheckedChange={(checked) => {
                          const setTiersFn = isMonthly ? setMonthlyTiers : setAnnualTiers;
                          const updatedTiers = [...tiers];
                          updatedTiers[index] = {
                            ...tier,
                            has_promotion: checked,
                            // Reset promotional fields if disabling
                            promotional_price: checked ? tier.promotional_price : undefined,
                            promotion_duration_months: checked ? tier.promotion_duration_months : undefined
                          };
                          setTiersFn(updatedTiers);
                        }}
                      />
                      <Label className="text-sm font-medium">
                        Enable limited-time promotion
                      </Label>
                    </div>

                      {tier.has_promotion && (
                        <div className="space-y-3 pl-7">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">Promotional Price *</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max={tier.base_price}
                                value={tier.promotional_price ?? ''}
                                onChange={(e) => {
                                  const setTiersFn = isMonthly ? setMonthlyTiers : setAnnualTiers;
                                  const promotionalPrice = parseFloat(e.target.value) || undefined;
                                  const updatedTiers = [...tiers];
                                  updatedTiers[index] = {
                                    ...tier,
                                    promotional_price: promotionalPrice,
                                    // Recalculate discount
                                    discount_percentage: calculateDiscountPercentage(promotionalPrice, tier.base_price)
                                  };
                                  setTiersFn(updatedTiers);
                                }}
                                placeholder={isMonthly ? "2.99" : "29.99"}
                                className="mt-1"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Actual price charged during promotion
                              </p>
                            </div>

                            <div>
                              <Label className="text-xs">Promotion Duration *</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  type="number"
                                  min="1"
                                  max="12"
                                  value={tier.promotion_duration_months || ''}
                                  onChange={(e) => {
                                    const setTiersFn = isMonthly ? setMonthlyTiers : setAnnualTiers;
                                    const updatedTiers = [...tiers];
                                    updatedTiers[index] = {
                                      ...tier,
                                      promotion_duration_months: parseInt(e.target.value) || undefined
                                    };
                                    setTiersFn(updatedTiers);
                                  }}
                                  placeholder="3"
                                  className="flex-1"
                                />
                                <span className="text-sm text-muted-foreground">{isMonthly ? 'months' : 'billing cycles'}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                How long the promotion lasts
                              </p>
                            </div>

                            <div>
                              <Label className="text-xs">Discount</Label>
                              <div className="h-10 flex items-center justify-center px-3 bg-background border rounded-md mt-1">
                                {tier.promotional_price !== undefined && tier.promotional_price > 0 && tier.base_price > 0 ? (
                                  <span className="text-sm font-semibold text-primary">
                                    {Math.round(((tier.base_price - tier.promotional_price) / tier.base_price) * 100)}%
                                  </span>
                                ) : (
                                  <span className="text-sm text-muted-foreground">—</span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Calculated
                              </p>
                            </div>
                          </div>

                          {/* Promotional Savings Display */}
                          {tier.promotional_price !== undefined &&
                           tier.promotional_price > 0 &&
                           tier.promotion_duration_months !== undefined &&
                           tier.promotion_duration_months > 0 && (
                            <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-900">
                              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                                <span className="text-sm font-medium">💰 Savings:</span>
                                <span className="text-sm">
                                  {baseInfo.currency_code}{(tier.base_price - tier.promotional_price).toFixed(2)}/{isMonthly ? 'month' : 'year'}
                                </span>
                              </div>
                              <div className="text-xs text-green-600 dark:text-green-500 mt-1">
                                📅 After {tier.promotion_duration_months} {isMonthly ? 'months' : 'billing cycles'}: {baseInfo.currency_code}{tier.base_price.toFixed(2)}/{isMonthly ? 'month' : 'year'}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Step 5: Verification
  const renderStep5 = () => {
    return (
      <div className="space-y-6 py-4 px-2">
        <div className="space-y-2">
          <Label className="text-lg font-semibold">Review Plans</Label>
          <p className="text-sm text-muted-foreground">Review all information before publishing</p>
        </div>

        {/* Base Info Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Base Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Plan Name</Label>
                <p className="font-medium">{baseInfo.name}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Currency</Label>
                <p className="font-medium">{baseInfo.currency_code}</p>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <p className="font-medium">{baseInfo.description}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Tax</Label>
                <p className="font-medium">
                  {baseInfo.tax_calculation === 'no_tax' 
                    ? 'No tax' 
                    : `${baseInfo.tax_percentage}% (${baseInfo.tax_calculation === 'include_tax' ? 'included' : 'added'})`}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Billing Cycles</Label>
                <p className="font-medium">{baseInfo.unlimited_cycles ? 'Unlimited' : `${baseInfo.total_cycles} cycles`}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Plan Review */}
        {(planSelection === 'monthly' || planSelection === 'both') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Monthly Plan
                <Badge variant="outline">Monthly</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tier Name</TableHead>
                      <TableHead className="text-right">Base Price</TableHead>
                      <TableHead className="text-right">Promotion</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyTiers.map((tier, index) => {
                      const discountPercentage = calculateDiscountPercentage(tier.promotional_price, tier.base_price);
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{tier.tier_name}</TableCell>
                          <TableCell className="text-right font-semibold">
                            {baseInfo.currency_code} {tier.base_price.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            {tier.has_promotion && tier.promotional_price && tier.promotion_duration_months ? (
                              <div className="text-xs">
                                <div className="font-semibold text-green-600">
                                  {baseInfo.currency_code} {tier.promotional_price.toFixed(2)}
                                </div>
                                <div className="text-muted-foreground">
                                  for {tier.promotion_duration_months}mo
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {discountPercentage !== undefined ? (
                              <span className="text-primary font-semibold">{discountPercentage}%</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {monthlyTiers.some(t => t.has_promotion) && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-900">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">
                    💰 Promotional Pricing Active
                  </p>
                  {monthlyTiers.filter(t => t.has_promotion).map((tier, idx) => (
                    <p key={idx} className="text-xs text-green-600 dark:text-green-500">
                      • {tier.tier_name}: {baseInfo.currency_code}{tier.promotional_price?.toFixed(2)}/month for {tier.promotion_duration_months} months,
                      then {baseInfo.currency_code}{tier.base_price.toFixed(2)}/month
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Annual Plan Review */}
        {(planSelection === 'annual' || planSelection === 'both') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Annual Plan
                <Badge variant="outline">Annual</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tier Name</TableHead>
                      <TableHead className="text-right">Base Price</TableHead>
                      <TableHead className="text-right">Annual Saving</TableHead>
                      <TableHead className="text-right">Promotion</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {annualTiers.map((tier, index) => {
                      const discountPercentage = calculateDiscountPercentage(tier.promotional_price, tier.base_price);
                      const monthlyTier = monthlyTiers.find(mt => mt.tier_name === tier.tier_name);
                      const annualSaving = monthlyTier ? calculateAnnualSaving(tier.base_price, monthlyTier.base_price) : undefined;
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{tier.tier_name}</TableCell>
                          <TableCell className="text-right font-semibold">
                            {baseInfo.currency_code} {tier.base_price.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            {annualSaving !== undefined ? (
                              <span className="text-green-600 font-semibold">{annualSaving}%</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {tier.has_promotion && tier.promotional_price && tier.promotion_duration_months ? (
                              <div className="text-xs">
                                <div className="font-semibold text-green-600">
                                  {baseInfo.currency_code} {tier.promotional_price.toFixed(2)}
                                </div>
                                <div className="text-muted-foreground">
                                  for {tier.promotion_duration_months}yr
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {discountPercentage !== undefined ? (
                              <span className="text-primary font-semibold">{discountPercentage}%</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {annualTiers.some(t => t.has_promotion) && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/20 rounded border border-green-200 dark:border-green-900">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">
                    💰 Promotional Pricing Active
                  </p>
                  {annualTiers.filter(t => t.has_promotion).map((tier, idx) => (
                    <p key={idx} className="text-xs text-green-600 dark:text-green-500">
                      • {tier.tier_name}: {baseInfo.currency_code}{tier.promotional_price?.toFixed(2)}/year for {tier.promotion_duration_months} years,
                      then {baseInfo.currency_code}{tier.base_price.toFixed(2)}/year
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const getStepTitle = (): string => {
    switch (currentStep) {
      case 1: return 'Select Plan Type';
      case 2: return 'Base Information';
      case 3: return 'Monthly Plan Pricing';
      case 4: return 'Annual Plan Pricing';
      case 5: return 'Review & Publish';
      default: return 'Create Plan';
    }
  };

  const getStepDescription = (): string => {
    switch (currentStep) {
      case 1: return 'Choose which subscription plans you want to create';
      case 2: return 'Shared settings that apply to all plans';
      case 3: return 'Define pricing tiers for the monthly plan';
      case 4: return 'Define pricing tiers for the annual plan';
      case 5: return 'Review all information before publishing';
      default: return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) resetWizard();
    }}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Tiered Plan
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{getStepTitle()}</DialogTitle>
          <DialogDescription>{getStepDescription()}</DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((step) => {
            const isActive = currentStep === step;
            const isCompleted = currentStep > step;
            const isVisible = step <= getTotalSteps();
            
            if (!isVisible) return null;
            
            return (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                  isActive ? 'bg-primary text-primary-foreground' : 
                  isCompleted ? 'bg-primary/20 text-primary' : 
                  'bg-muted text-muted-foreground'
                }`}>
                  {isCompleted ? <Check className="h-4 w-4" /> : step}
                </div>
                {step < getTotalSteps() && (
                  <div className={`h-1 w-12 transition-colors ${
                    currentStep > step ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderTiersStep(true)}
          {currentStep === 4 && renderTiersStep(false)}
          {currentStep === 5 && renderStep5()}
        </div>

        <DialogFooter>
          <div className="flex justify-between w-full">
            <Button 
              variant="outline" 
              onClick={() => {
                if (currentStep === 1) {
                  setIsOpen(false);
                  resetWizard();
                } else {
                  handleBack();
                }
              }} 
              disabled={loading}
            >
              {currentStep === 1 ? 'Cancel' : (
                <>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </>
              )}
            </Button>
            <div className="flex gap-2">
              {currentStep < getTotalSteps() && (
                <Button onClick={handleNext} disabled={loading}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              {currentStep === getTotalSteps() && (
                <Button onClick={handleCreatePlans} disabled={loading}>
                  {loading ? 'Publishing...' : 'Publish Plans'}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTieredPlanModal;
